import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { ProductLockup } from '../components/brand/Brand'
import { inviteOrganisationUser } from '../lib/governance-api'
import {
  completeOnboarding,
  createBusinessArea,
  loadOnboardingState,
  retireBusinessArea,
  saveOnboardingSetup,
  setGovernanceOwner,
  setOnboardingAccountabilityInvitation,
  startOrResumeOnboarding,
  updateOnboardingOrganisationProfile,
  type OnboardingState,
} from '../onboarding/onboarding-api'
import { useTenant } from '../tenancy/TenantProvider'

const totalSetupSteps = 4

const stepMeta: Record<number, { position: number; label: string }> = {
  0: { position: 0, label: 'Welcome' },
  1: { position: 1, label: 'Your organisation' },
  2: { position: 2, label: 'Governance responsibility' },
  5: { position: 3, label: 'Business areas' },
  8: { position: 4, label: 'Ready' },
}

const suggestedBusinessAreas = [
  'Finance',
  'Human Resources',
  'Operations',
  'Manufacturing',
  'Technology',
  'Procurement',
  'Sales & Marketing',
  'Customer Service',
  'Risk & Compliance',
]

const australianLocations = [
  ['Adelaide', 'South Australia'],
  ['Albany', 'Western Australia'],
  ['Albury', 'New South Wales'],
  ['Alice Springs', 'Northern Territory'],
  ['Ballarat', 'Victoria'],
  ['Bendigo', 'Victoria'],
  ['Brisbane', 'Queensland'],
  ['Broome', 'Western Australia'],
  ['Bundaberg', 'Queensland'],
  ['Bunbury', 'Western Australia'],
  ['Cairns', 'Queensland'],
  ['Canberra', 'Australian Capital Territory'],
  ['Coffs Harbour', 'New South Wales'],
  ['Darwin', 'Northern Territory'],
  ['Devonport', 'Tasmania'],
  ['Dubbo', 'New South Wales'],
  ['Geelong', 'Victoria'],
  ['Geraldton', 'Western Australia'],
  ['Gladstone', 'Queensland'],
  ['Gold Coast', 'Queensland'],
  ['Gosford', 'New South Wales'],
  ['Hervey Bay', 'Queensland'],
  ['Hobart', 'Tasmania'],
  ['Kalgoorlie', 'Western Australia'],
  ['Karratha', 'Western Australia'],
  ['Launceston', 'Tasmania'],
  ['Mackay', 'Queensland'],
  ['Melbourne', 'Victoria'],
  ['Mildura', 'Victoria'],
  ['Mount Gambier', 'South Australia'],
  ['Newcastle', 'New South Wales'],
  ['Orange', 'New South Wales'],
  ['Perth', 'Western Australia'],
  ['Port Hedland', 'Western Australia'],
  ['Port Macquarie', 'New South Wales'],
  ['Rockhampton', 'Queensland'],
  ['Shepparton', 'Victoria'],
  ['Sunshine Coast', 'Queensland'],
  ['Sydney', 'New South Wales'],
  ['Tamworth', 'New South Wales'],
  ['Toowoomba', 'Queensland'],
  ['Townsville', 'Queensland'],
  ['Wagga Wagga', 'New South Wales'],
  ['Wollongong', 'New South Wales'],
] as const

const australianStates = [
  'Australian Capital Territory',
  'New South Wales',
  'Northern Territory',
  'Queensland',
  'South Australia',
  'Tasmania',
  'Victoria',
  'Western Australia',
]

const countryOptions = [
  ['AU', 'Australia'],
  ['NZ', 'New Zealand'],
  ['SG', 'Singapore'],
  ['GB', 'United Kingdom'],
  ['US', 'United States'],
  ['CA', 'Canada'],
] as const

type OwnerMode = 'self' | 'existing' | 'invite' | 'later'

function looksLikeEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

function visibleStepFromPersisted(step: number | null | undefined) {
  if (typeof step !== 'number' || step <= 0) return 0
  if (step === 1) return 1
  if (step === 2) return 2
  if (step >= 3 && step <= 5) return 5
  return 8
}

function parseAustralianLocation(value: string) {
  const trimmed = value.trim()
  const match = australianLocations.find(([city, state]) => `${city}, ${state}`.toLowerCase() === trimmed.toLowerCase())
  if (match) return { locality: match[0], stateRegion: match[1] }

  const [possibleCity, possibleState] = trimmed.split(',').map((part) => part.trim())
  return {
    locality: possibleCity || trimmed,
    stateRegion: possibleState || '',
  }
}

export function OnboardingPage() {
  const { current, loading, refreshOrganisations, hasRole } = useTenant()
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const organisationId = current?.organisation.id ?? ''
  const canAdminister = hasRole('organisation_admin')
  const firstRunReplayRef = useRef(false)

  const [step, setStep] = useState(0)
  const [firstRunReplay, setFirstRunReplay] = useState(false)
  const [name, setName] = useState('')
  const [governanceContactEmail, setGovernanceContactEmail] = useState('')
  const [locationEntry, setLocationEntry] = useState('')
  const [stateRegion, setStateRegion] = useState('')
  const [countryCode, setCountryCode] = useState('AU')
  const [ownerId, setOwnerId] = useState('')
  const [ownerMode, setOwnerMode] = useState<OwnerMode>('later')
  const [ownerInviteName, setOwnerInviteName] = useState('')
  const [ownerInviteEmail, setOwnerInviteEmail] = useState('')
  const [businessAreaName, setBusinessAreaName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const stateQuery = useQuery({
    queryKey: ['organisation-onboarding', organisationId],
    queryFn: async () => {
      await startOrResumeOnboarding(organisationId)
      return loadOnboardingState(organisationId)
    },
    enabled: Boolean(organisationId),
  })

  const state = stateQuery.data
  const currentStepMeta = stepMeta[step] ?? { position: 0, label: 'Welcome' }
  const progressPercent = step === 0 ? 4 : (currentStepMeta.position / totalSetupSteps) * 100

  useEffect(() => {
    if (!state) return

    if (!firstRunReplayRef.current) {
      setStep(visibleStepFromPersisted(state.setup.currentStep))
    }

    setName(looksLikeEmail(state.organisation.name) ? '' : state.organisation.name)
    setGovernanceContactEmail(state.organisation.governanceContactEmail ?? user?.email ?? '')
    setCountryCode(state.organisation.countryCode || 'AU')
    setStateRegion(state.organisation.stateRegion ?? '')

    if (state.organisation.locality) {
      setLocationEntry(
        state.organisation.countryCode === 'AU' && state.organisation.stateRegion
          ? `${state.organisation.locality}, ${state.organisation.stateRegion}`
          : state.organisation.locality,
      )
    } else {
      setLocationEntry('')
    }

    setOwnerId(state.governanceOwner?.membershipId ?? '')

    if (state.governanceOwnerInvitation) {
      setOwnerMode('invite')
      setOwnerInviteName(state.governanceOwnerInvitation.displayName ?? '')
      setOwnerInviteEmail(state.governanceOwnerInvitation.email)
    } else if (state.governanceOwner?.membershipId === current?.membershipId) {
      setOwnerMode('self')
    } else if (state.governanceOwner) {
      setOwnerMode('existing')
    } else {
      setOwnerMode('later')
    }
  }, [current?.membershipId, state, user?.email])

  const refreshState = async () => {
    await queryClient.invalidateQueries({ queryKey: ['organisation-onboarding', organisationId] })
  }

  const saveSetup = async (
    nextStep: number,
    overrides?: Partial<{
      businessAreasStepStatus: OnboardingState['setup']['businessAreasStepStatus']
      technologyStepStatus: OnboardingState['setup']['technologyStepStatus']
    }>,
  ) => {
    if (!state) return

    const persistedStep = state.organisation.onboardingStatus === 'baseline_complete'
      ? state.setup.currentStep
      : nextStep

    await saveOnboardingSetup({
      organisationId,
      currentStep: persistedStep,
      aiPolicyStatus: state.setup.aiPolicyStatus,
      aiStrategyStatus: state.setup.aiStrategyStatus,
      aiRegisterStatus: state.setup.aiRegisterStatus,
      governanceProcessStatus: state.setup.governanceProcessStatus,
      approvalAuthoritiesStatus: state.setup.approvalAuthoritiesStatus,
      preferredStart: state.setup.preferredStart,
      businessAreasStepStatus: overrides?.businessAreasStepStatus ?? state.setup.businessAreasStepStatus,
      technologyStepStatus: overrides?.technologyStepStatus ?? state.setup.technologyStepStatus,
    })
  }

  const ownerInviteMutation = useMutation({
    mutationFn: async () => {
      if (!ownerInviteName.trim()) throw new Error('Add the person’s name so the pending assignment is clear.')
      if (!ownerInviteEmail.trim()) throw new Error('Add an email address for the Governance Coordinator invitation.')

      const result = await inviteOrganisationUser({
        organisationId,
        displayName: ownerInviteName.trim(),
        email: ownerInviteEmail.trim(),
        roles: [],
      })

      await setOnboardingAccountabilityInvitation({
        organisationId,
        accountabilityRole: 'governance_owner',
        invitationId: result.invitationId,
        displayName: ownerInviteName.trim(),
      })

      return result
    },
    onSuccess: refreshState,
    onError: (mutationError) => {
      setError(mutationError instanceof Error ? mutationError.message : 'We could not send that invitation.')
    },
  })

  const continueMutation = useMutation({
    mutationFn: async () => {
      setError(null)
      if (!state) return

      if (step === 0) {
        await saveSetup(1)
        setStep(1)
        return
      }

      if (step === 1) {
        if (!name.trim()) throw new Error('Organisation name is required.')
        if (looksLikeEmail(name)) throw new Error('Enter the organisation name rather than an email address.')
        if (!governanceContactEmail.trim() || !governanceContactEmail.includes('@')) {
          throw new Error('A valid governance contact email is required.')
        }
        if (!locationEntry.trim()) throw new Error('City or locality is required.')

        const parsedLocation = countryCode === 'AU'
          ? parseAustralianLocation(locationEntry)
          : { locality: locationEntry.trim(), stateRegion }

        const nextStateRegion = parsedLocation.stateRegion || stateRegion
        if (!nextStateRegion.trim()) throw new Error('State, territory or region is required.')

        await updateOnboardingOrganisationProfile({
          organisationId,
          name: name.trim(),
          governanceContactEmail: governanceContactEmail.trim().toLowerCase(),
          locality: parsedLocation.locality,
          stateRegion: nextStateRegion.trim(),
          countryCode,
        })

        setStateRegion(nextStateRegion.trim())
        await saveSetup(2)
        setStep(2)
        await refreshState()
        return
      }

      if (step === 2) {
        if (canAdminister) {
          if (ownerMode === 'self') {
            if (!current?.membershipId) throw new Error('Your organisation membership could not be identified.')
            await setGovernanceOwner(organisationId, current.membershipId)
            await setOnboardingAccountabilityInvitation({
              organisationId,
              accountabilityRole: 'governance_owner',
              invitationId: null,
            })
          } else if (ownerMode === 'existing') {
            const selectedPerson = state.people.find((person) => (
              person.membershipId === ownerId
              && person.membershipId !== current?.membershipId
              && person.status === 'active'
            ))
            if (!selectedPerson) throw new Error('Choose an active existing user or select Set up later.')
            await setGovernanceOwner(organisationId, ownerId)
            await setOnboardingAccountabilityInvitation({
              organisationId,
              accountabilityRole: 'governance_owner',
              invitationId: null,
            })
          } else if (ownerMode === 'invite') {
            if (!state.governanceOwnerInvitation) {
              throw new Error('Send the Governance Coordinator invitation before continuing, or select Set up later.')
            }
          } else if (state.governanceOwnerInvitation) {
            await setOnboardingAccountabilityInvitation({
              organisationId,
              accountabilityRole: 'governance_owner',
              invitationId: null,
            })
          }
        }

        await saveSetup(5)
        setStep(5)
        await refreshState()
        return
      }

      if (step === 5) {
        await saveSetup(8, {
          businessAreasStepStatus: state.businessAreas.length > 0 ? 'complete' : 'skipped',
        })
        setStep(8)
        await refreshState()
      }
    },
    onError: (mutationError) => {
      setError(mutationError instanceof Error ? mutationError.message : 'We could not save this step.')
    },
  })

  const businessAreaMutation = useMutation({
    mutationFn: async (areaName: string) => {
      const trimmed = areaName.trim()
      if (!trimmed) return
      await createBusinessArea(organisationId, trimmed)
      setBusinessAreaName('')
      await refreshState()
    },
    onError: (mutationError) => {
      setError(mutationError instanceof Error ? mutationError.message : 'We could not add that business area.')
    },
  })

  const retireBusinessAreaMutation = useMutation({
    mutationFn: async (businessAreaId: string) => {
      setError(null)
      await retireBusinessArea(organisationId, businessAreaId)
      await refreshState()
    },
    onError: (mutationError) => {
      setError(mutationError instanceof Error ? mutationError.message : 'We could not remove that business area.')
    },
  })

  const completionMutation = useMutation({
    mutationFn: async () => {
      const completed = await completeOnboarding(organisationId)
      await refreshOrganisations()
      return completed
    },
    onSuccess: () => {
      void navigate('/', { replace: true })
    },
    onError: (mutationError) => {
      setError(mutationError instanceof Error ? mutationError.message : 'We could not complete organisation setup.')
    },
  })

  if (loading) return <main className="onboarding-page onboarding-centred">Loading your organisation…</main>

  if (!current) {
    return (
      <main className="onboarding-page onboarding-centred">
        <ProductLockup compact />
        <h1>No organisation is available yet</h1>
        <p>Your Swell environment needs to be provisioned before organisation setup can begin.</p>
      </main>
    )
  }

  if (!canAdminister && !hasRole('governance_manager')) return <Navigate to="/" replace />
  if (stateQuery.isLoading) return <main className="onboarding-page onboarding-centred">Preparing your setup…</main>
  if (stateQuery.isError || !state) {
    return (
      <main className="onboarding-page onboarding-centred">
        <ProductLockup compact />
        <h1>We couldn’t load your organisation setup</h1>
        <p>We couldn’t confirm the active people in this organisation. Nothing has been changed. Please try again.</p>
        <button className="button primary" type="button" onClick={() => void stateQuery.refetch()}>Retry</button>
      </main>
    )
  }

  const activePeople = state.people.filter((person) => person.status === 'active')
  const eligibleExistingPeople = activePeople.filter((person) => person.membershipId !== current.membershipId)
  const savedCoordinatorIsActive = state.governanceOwner
    ? activePeople.some((person) => person.membershipId === state.governanceOwner?.membershipId)
    : true
  const savedExistingCoordinatorUnavailable = ownerMode === 'existing' && !savedCoordinatorIsActive
  const currentAdministrator = activePeople.find((person) => person.membershipId === current.membershipId)
  const availableSuggestions = suggestedBusinessAreas.filter(
    (suggestion) => !state.businessAreas.some((area) => area.name.toLowerCase() === suggestion.toLowerCase()),
  )

  const goBack = () => {
    setError(null)
    setStep((currentStep) => {
      if (currentStep === 8) return 5
      if (currentStep === 5) return 2
      if (currentStep === 2) return 1
      return 0
    })
  }

  const reviewSetup = () => {
    setError(null)
    firstRunReplayRef.current = true
    setFirstRunReplay(false)
    setStep(1)
  }

  const beginFirstRunReplay = () => {
    setError(null)
    firstRunReplayRef.current = true
    setFirstRunReplay(true)
    setStep(0)
  }

  const finishFirstRunReplay = () => {
    firstRunReplayRef.current = false
    setFirstRunReplay(false)
    void navigate('/', { replace: true })
  }

  const leaveSetup = () => void navigate('/', { replace: true })

  const handleLocationChange = (value: string) => {
    setLocationEntry(value)
    if (countryCode !== 'AU') return

    const match = australianLocations.find(([city, region]) => (
      `${city}, ${region}`.toLowerCase() === value.trim().toLowerCase()
    ))
    if (match) setStateRegion(match[1])
  }

  const coordinatorSummary = state.governanceOwner
    ? state.governanceOwner.displayName
    : state.governanceOwnerInvitation
      ? `${state.governanceOwnerInvitation.displayName ?? state.governanceOwnerInvitation.email} — invitation pending`
      : 'Not assigned yet'

  const locationSummary = [
    state.organisation.locality,
    state.organisation.stateRegion,
    countryOptions.find(([code]) => code === state.organisation.countryCode)?.[1] ?? state.organisation.countryCode,
  ].filter(Boolean).join(', ')

  return (
    <main className="onboarding-page phase2-onboarding streamlined-onboarding">
      <header className="onboarding-header">
        <ProductLockup compact />
        <div className="onboarding-progress-copy">
          {step === 0 ? 'Organisation setup' : `${currentStepMeta.position} of ${totalSetupSteps} · ${currentStepMeta.label}`}
        </div>
        {state.organisation.onboardingStatus === 'baseline_complete' ? (
          <button className="button ghost" type="button" onClick={leaveSetup}>Exit setup</button>
        ) : <span />}
      </header>

      <div className="onboarding-progress-track" aria-hidden="true">
        <span style={{ width: `${Math.max(4, progressPercent)}%` }} />
      </div>

      <section className={`onboarding-stage ${step === 0 || step === 8 ? 'onboarding-stage-hero' : ''}`}>
        {step === 0 ? (
          <div className="onboarding-welcome streamlined-welcome">
            <div className="onboarding-brand-orbit" aria-hidden="true">
              <img src="/brand/fifthwave-mark.svg" alt="" />
              <span className="onboarding-brand-orbit-accent" />
            </div>
            <p className="eyebrow">Welcome to Swell AI</p>
            <h1>Set up your AI governance without starting from scratch.</h1>
            <p className="onboarding-lead">
              We will ask a few simple questions about your organisation, who coordinates AI governance,
              and the business areas you want to start with. You do not need every answer today.
            </p>
            <div className="onboarding-primary-start">
              <button
                className="button primary"
                type="button"
                onClick={() => continueMutation.mutate()}
                disabled={continueMutation.isPending}
              >
                {continueMutation.isPending ? 'Starting…' : 'Start organisation setup'}
              </button>
            </div>
            <div className="onboarding-expectations">
              <p className="eyebrow">What to expect</p>
              <div className="onboarding-promise-grid onboarding-information-panels">
                <div><strong>Start with what you know</strong><span>You do not need every answer today. Unknown is a valid response.</span></div>
                <div><strong>Come back later</strong><span>Your progress is saved and outstanding setup items remain visible.</span></div>
                <div><strong>Build progressively</strong><span>Setup gives Strategy, the AI Register and later governance work useful organisational context.</span></div>
              </div>
            </div>
          </div>
        ) : null}

        {step === 1 ? (
          <div className="onboarding-form-shell">
            <p className="eyebrow">Step 1</p>
            <h1>Let’s confirm your organisation.</h1>
            <p className="onboarding-lead">
              We only need a few details to get Swell ready for your organisation. You can update these later in Organisation settings.
            </p>

            <div className="onboarding-form-grid">
              <label className="onboarding-field onboarding-field-wide">
                <span>Organisation name</span>
                <input
                  type="text"
                  autoComplete="organization"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Example: Acme Manufacturing"
                  required
                />
                <small>Confirm the organisation Swell has been provisioned for. An email address is not used as an organisation name.</small>
              </label>

              <label className="onboarding-field onboarding-field-wide">
                <span>Governance contact email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={governanceContactEmail}
                  onChange={(event) => setGovernanceContactEmail(event.target.value)}
                  placeholder="governance@example.com"
                  required
                />
                <small>Swell uses this address for important organisation-level governance and service communications. You can change it later.</small>
              </label>

              <label className="onboarding-field">
                <span>City or locality</span>
                <input
                  type="text"
                  list={countryCode === 'AU' ? 'australian-localities' : undefined}
                  value={locationEntry}
                  onChange={(event) => handleLocationChange(event.target.value)}
                  placeholder={countryCode === 'AU' ? 'Start typing, for example Bris…' : 'City or locality'}
                  autoComplete="address-level2"
                  required
                />
                {countryCode === 'AU' ? (
                  <datalist id="australian-localities">
                    {australianLocations.map(([city, region]) => (
                      <option key={`${city}-${region}`} value={`${city}, ${region}`} />
                    ))}
                  </datalist>
                ) : null}
                <small>You can type a locality even if it is not in the suggestions.</small>
              </label>

              <label className="onboarding-field">
                <span>{countryCode === 'AU' ? 'State or territory' : 'State, territory or region'}</span>
                {countryCode === 'AU' ? (
                  <select value={stateRegion} onChange={(event) => setStateRegion(event.target.value)} required>
                    <option value="">Select…</option>
                    {australianStates.map((region) => <option key={region} value={region}>{region}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={stateRegion}
                    onChange={(event) => setStateRegion(event.target.value)}
                    autoComplete="address-level1"
                    required
                  />
                )}
              </label>

              <label className="onboarding-field">
                <span>Country</span>
                <select
                  value={countryCode}
                  onChange={(event) => {
                    const nextCountry = event.target.value
                    setCountryCode(nextCountry)
                    if (nextCountry !== 'AU') setStateRegion('')
                  }}
                >
                  {countryOptions.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                </select>
              </label>
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="onboarding-form-shell">
            <p className="eyebrow">Step 2</p>
            <h1>Who will look after AI governance?</h1>
            <p className="onboarding-lead">
              Swell uses these roles to direct governance work and reminders to the right people.
              If your organisation has not decided this yet, you can complete it later.
            </p>

            <div className="accountability-stack streamlined-accountability">
              <section className="accountability-card">
                <div className="accountability-heading">
                  <div>
                    <h2>Organisation Administrator</h2>
                    <p>Manages Swell access, users and organisation settings.</p>
                  </div>
                </div>
                <div className="onboarding-known-person">
                  <strong>{currentAdministrator?.displayName ?? user?.email ?? 'Current administrator'}</strong>
                  <span>Already known from your Swell access</span>
                </div>
              </section>

              <section className="accountability-card">
                <div className="accountability-heading">
                  <div>
                    <h2>Governance Coordinator <small>Optional during setup</small></h2>
                    <p>The person who keeps AI governance activity moving and helps make sure assessments, actions and reviews reach the right people.</p>
                  </div>
                </div>

                {!canAdminister ? (
                  <div className="inline-alert neutral">
                    An Organisation Administrator can assign or change the Governance Coordinator later.
                  </div>
                ) : (
                  <>
                    <div className="onboarding-role-options" role="radiogroup" aria-label="Governance Coordinator choice">
                      <label>
                        <input type="radio" name="owner-mode" checked={ownerMode === 'self'} onChange={() => setOwnerMode('self')} />
                        <span><strong>I’ll coordinate</strong><small>Assign this responsibility to me.</small></span>
                      </label>
                      {eligibleExistingPeople.length > 0 ? (
                        <label>
                          <input type="radio" name="owner-mode" checked={ownerMode === 'existing'} onChange={() => setOwnerMode('existing')} />
                          <span><strong>Choose an existing user</strong><small>Select another active person who already has access to this organisation in Swell.</small></span>
                        </label>
                      ) : null}
                      <label>
                        <input type="radio" name="owner-mode" checked={ownerMode === 'invite'} onChange={() => setOwnerMode('invite')} />
                        <span><strong>Invite someone</strong><small>Send access now and nominate them as the pending coordinator.</small></span>
                      </label>
                      <label>
                        <input type="radio" name="owner-mode" checked={ownerMode === 'later'} onChange={() => setOwnerMode('later')} />
                        <span><strong>Set up later</strong><small>Continue without assigning a Governance Coordinator today.</small></span>
                      </label>
                    </div>

                    {savedExistingCoordinatorUnavailable ? (
                      <div className="inline-alert warning" role="alert">
                        The saved Governance Coordinator is no longer available. Choose an active person, invite someone, or set up later.
                      </div>
                    ) : null}

                    {ownerMode === 'existing' && eligibleExistingPeople.length > 0 ? (
                      <label className="onboarding-inline-field">
                        <span>Existing user</span>
                        <select value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>
                          <option value="">Choose a user…</option>
                          {eligibleExistingPeople.map((person) => (
                            <option key={person.membershipId} value={person.membershipId}>{person.displayName}</option>
                          ))}
                        </select>
                      </label>
                    ) : null}

                    {ownerMode === 'invite' ? (
                      <div className="onboarding-invite-panel">
                        {state.governanceOwnerInvitation ? (
                          <div className="pending-assignment">
                            <strong>{state.governanceOwnerInvitation.displayName ?? state.governanceOwnerInvitation.email}</strong>
                            <span>{state.governanceOwnerInvitation.email} · invitation pending</span>
                          </div>
                        ) : (
                          <>
                            <div className="onboarding-invite-fields">
                              <label><span>Name</span><input value={ownerInviteName} onChange={(event) => setOwnerInviteName(event.target.value)} /></label>
                              <label><span>Email</span><input type="email" value={ownerInviteEmail} onChange={(event) => setOwnerInviteEmail(event.target.value)} /></label>
                            </div>
                            <button
                              className="button secondary"
                              type="button"
                              onClick={() => ownerInviteMutation.mutate()}
                              disabled={ownerInviteMutation.isPending}
                            >
                              {ownerInviteMutation.isPending ? 'Sending…' : 'Send invitation'}
                            </button>
                          </>
                        )}
                      </div>
                    ) : null}
                  </>
                )}

                <p className="choice-explainer">You can assign or change this responsibility later in Swell. Swell does not assume one organisation-wide AI approver; approval responsibility is handled in the relevant governance context.</p>
              </section>
            </div>
          </div>
        ) : null}

        {step === 5 ? (
          <div className="onboarding-form-shell">
            <p className="eyebrow">Step 3</p>
            <h1>How is your organisation structured?</h1>
            <p className="onboarding-lead">
              Business areas help Swell organise AI uses, assign responsibility and produce meaningful reports.
              For example, an AI use could belong to Finance, Human Resources or Manufacturing.
            </p>
            <p className="choice-explainer">
              You do not need to map your whole organisation now. Add the main areas you want to start with.
              You can add, rename or reorganise them later.
            </p>

            {availableSuggestions.length > 0 ? (
              <div className="business-area-suggestions">
                <span>Suggested business areas</span>
                <div className="onboarding-suggestion-row">
                  {availableSuggestions.map((area) => (
                    <button
                      className="button secondary compact"
                      type="button"
                      key={area}
                      onClick={() => businessAreaMutation.mutate(area)}
                      disabled={businessAreaMutation.isPending}
                    >
                      + {area}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="onboarding-add-area">
              <label className="onboarding-field">
                <span>Add another business area</span>
                <input
                  type="text"
                  value={businessAreaName}
                  onChange={(event) => setBusinessAreaName(event.target.value)}
                  placeholder="Example: Community Services"
                />
              </label>
              <button
                className="button secondary"
                type="button"
                onClick={() => businessAreaMutation.mutate(businessAreaName)}
                disabled={!businessAreaName.trim() || businessAreaMutation.isPending}
              >
                Add business area
              </button>
            </div>

            <div className="business-area-list" aria-label="Business areas added">
              {state.businessAreas.length === 0 ? (
                <div className="inline-alert neutral">No business areas added yet. You can skip this step and add them later.</div>
              ) : state.businessAreas.map((area) => (
                <div className="business-area-chip" key={area.id}>
                  <span>{area.name}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${area.name}`}
                    title={`Remove ${area.name}`}
                    onClick={() => retireBusinessAreaMutation.mutate(area.id)}
                    disabled={retireBusinessAreaMutation.isPending}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {step === 8 ? (
          <div className="onboarding-ready streamlined-ready">
            <p className="eyebrow">Step 4</p>
            <h1>Your organisation is ready.</h1>
            <p className="onboarding-lead">
              We have enough information to get you started. You can continue building your governance framework as you use Swell.
            </p>

            <div className="onboarding-summary-list">
              <div><span>Organisation</span><strong>{state.organisation.name}</strong></div>
              <div><span>Location</span><strong>{locationSummary || 'Not recorded'}</strong></div>
              <div><span>Organisation Administrator</span><strong>{currentAdministrator?.displayName ?? user?.email ?? 'Current administrator'}</strong></div>
              <div><span>Governance Coordinator</span><strong>{coordinatorSummary}</strong></div>
              <div>
                <span>Business areas</span>
                <strong>{state.businessAreas.length > 0 ? state.businessAreas.map((area) => area.name).join(', ') : 'None added yet'}</strong>
              </div>
            </div>

            {error ? <div className="inline-alert danger">{error}</div> : null}

            <div className="onboarding-ready-actions">
              <button
                className="button primary"
                type="button"
                onClick={() => {
                  if (firstRunReplay && state.organisation.onboardingStatus === 'baseline_complete') {
                    finishFirstRunReplay()
                  } else {
                    completionMutation.mutate()
                  }
                }}
                disabled={completionMutation.isPending}
              >
                {completionMutation.isPending ? 'Finishing…' : firstRunReplay ? 'Return to Swell AI' : 'Go to Swell AI'}
              </button>
              <button className="button secondary" type="button" onClick={reviewSetup}>Review setup</button>
              {state.organisation.onboardingStatus === 'baseline_complete' && canAdminister && !firstRunReplay ? (
                <button className="button text" type="button" onClick={beginFirstRunReplay}>Replay first-run experience</button>
              ) : null}
            </div>
          </div>
        ) : null}

        {error && step !== 8 ? <div className="inline-alert danger onboarding-error">{error}</div> : null}
      </section>

      {step > 0 && step < 8 ? (
        <footer className="onboarding-actions">
          <button className="button ghost" type="button" onClick={goBack}>Back</button>
          <div className="onboarding-action-group">
            {step === 5 && state.businessAreas.length === 0 ? (
              <button
                className="button text"
                type="button"
                onClick={() => continueMutation.mutate()}
                disabled={continueMutation.isPending}
              >
                Skip for now
              </button>
            ) : null}
            <button
              className="button primary"
              type="button"
              onClick={() => continueMutation.mutate()}
              disabled={continueMutation.isPending}
            >
              {continueMutation.isPending ? 'Saving…' : step === 5 ? 'Continue' : 'Save and continue'}
            </button>
          </div>
        </footer>
      ) : null}
    </main>
  )
}
